import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, NavLink, Outlet, useLocation } from 'react-router-dom';
import { 
  FiHome, 
  FiUsers, 
  FiTruck, 
  FiDollarSign, 
  FiSettings, 
  FiLogOut,
  FiMenu,
  FiX,
  FiBell,
  FiUser,
  FiChevronRight,
  FiMessageSquare,
  FiHelpCircle,
  FiBarChart2,
  FiFileText,
  FiBell as FiNotifications,
  FiGrid,
  FiActivity,
  FiShield,
  FiUserCheck,
  FiCreditCard,
  FiSettings as FiSystemConfig,
  FiShield as FiRolesPermissions,
  FiMail as FiEmailTemplates,
  FiActivity as FiSystemMonitoring,
  FiTag,
  FiStar,
  FiTrendingUp,
  FiLayers,
  FiPercent,
  FiCheckCircle,
  FiUserPlus,
  FiAlertOctagon
} from 'react-icons/fi';
import { api } from '../../services/api';
import { socketService } from '../../utils/socketService';

function AdminLayout() {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [notificationPermission, setNotificationPermission] = useState('default');
  /** Alertes chat (nouveaux messages) — séparées des alertes plateforme. */
  const [messageNotifications, setMessageNotifications] = useState([]);
  /** Réclamations, inscriptions déménageur, etc. — sans mélange avec les messages. */
  const [platformNotifications, setPlatformNotifications] = useState([]);
  const [showNotificationDropdown, setShowNotificationDropdown] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const lastMessageIdsRef = useRef(new Map()); // Pour suivre les derniers messages par conversation
  const firstConversationPollRef = useRef(true); // évite une rafale de notifs au premier chargement
  const conversationsRef = useRef([]);
  const notificationDropdownRef = useRef(null);
  
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  const navigateForPlatformKind = useCallback(
    (kind, notif) => {
      if (kind === 'reclamation') {
        navigate('/admin/tickets');
        return;
      }
      if (kind === 'mover_registration') {
        navigate(
          notif?.moverId
            ? `/admin/mover-registration/${notif.moverId}`
            : '/admin/account-verification'
        );
        return;
      }
      if (kind === 'client_registration') {
        navigate(notif?.clientId ? `/admin/users` : '/admin/users');
        return;
      }
      if (kind === 'mover_auto_banned') {
        navigate(notif?.moverId ? `/admin/users` : '/admin/users');
        return;
      }
      if (kind === 'content_report') {
        navigate('/admin/moderation');
      }
    },
    [navigate]
  );

  // Organisation des éléments du menu par groupes logiques avec séparateurs visuels
  const menuGroups = [
    {
      id: 'overview',
      items: [
    { id: 'home', to: '/admin', label: 'Tableau de bord', icon: <FiHome className="w-5 h-5" /> },
      ]
    },
    {
      id: 'management',
      label: 'Gestion',
      items: [
    { id: 'users', to: '/admin/users', label: 'Utilisateurs', icon: <FiUsers className="w-5 h-5" /> },
        { id: 'account-verification', to: '/admin/account-verification', label: 'Vérification comptes', icon: <FiCheckCircle className="w-5 h-5" /> },
      ]
    },
    {
      id: 'business',
      label: 'Transactions',
      items: [
    { id: 'moves', to: '/admin/moves', label: 'Déménagements', icon: <FiTruck className="w-5 h-5" /> },
        { id: 'negotiations', to: '/admin/negotiations', label: 'Négociations', icon: <FiPercent className="w-5 h-5" /> },
    { id: 'payments', to: '/admin/payments', label: 'Paiements', icon: <FiDollarSign className="w-5 h-5" /> },
        { id: 'subscriptions', to: '/admin/subscriptions', label: 'Abonnements', icon: <FiCreditCard className="w-5 h-5" /> },
      ]
    },
    {
      id: 'communication',
      label: 'Communication',
      items: [
    { id: 'messages', to: '/admin/messages', label: 'Messages', icon: <FiMessageSquare className="w-5 h-5" /> },
        { id: 'tickets', to: '/admin/tickets', label: 'Tickets', icon: <FiHelpCircle className="w-5 h-5" /> },
      ]
    },
    {
      id: 'marketing',
      label: 'Marketing',
      items: [
        { id: 'marketing', to: '/admin/marketing', label: 'Marketing', icon: <FiTrendingUp className="w-5 h-5" /> },
        { id: 'referrals', to: '/admin/referrals', label: 'Parrainage', icon: <FiUserPlus className="w-5 h-5" /> },
        { id: 'reviews', to: '/admin/reviews', label: 'Avis', icon: <FiStar className="w-5 h-5" /> },
        { id: 'categories', to: '/admin/categories', label: 'Types de service', icon: <FiLayers className="w-5 h-5" /> },
      ]
    },
    {
      id: 'analytics',
      label: 'Analytics',
      items: [
        { id: 'analytics', to: '/admin/analytics', label: 'Analyses', icon: <FiBarChart2 className="w-5 h-5" /> },
        { id: 'reports', to: '/admin/reports', label: 'Rapports', icon: <FiFileText className="w-5 h-5" /> },
      ]
    },
    {
      id: 'security',
      label: 'Sécurité',
      items: [
        { id: 'moderation', to: '/admin/moderation', label: 'Modération', icon: <FiUserCheck className="w-5 h-5" /> },
        { id: 'frauds', to: '/admin/frauds', label: 'Détection fraudes', icon: <FiShield className="w-5 h-5" /> },
      ]
    },
    {
      id: 'system',
      label: 'Système',
      items: [
        { id: 'system-monitoring', to: '/admin/system-monitoring', label: 'Monitoring', icon: <FiSystemMonitoring className="w-5 h-5" /> },
        { id: 'system-config', to: '/admin/system-config', label: 'Configuration', icon: <FiSystemConfig className="w-5 h-5" /> },
        { id: 'roles-permissions', to: '/admin/roles-permissions', label: 'Rôles & Permissions', icon: <FiRolesPermissions className="w-5 h-5" /> },
      ]
    },
    {
      id: 'administration',
      label: 'Administration',
      items: [
        { id: 'activity-logs', to: '/admin/activity-logs', label: 'Journal activité', icon: <FiActivity className="w-5 h-5" /> },
        { id: 'logs', to: '/admin/logs', label: 'Logs système', icon: <FiBell className="w-5 h-5" /> },
        { id: 'notifications', to: '/admin/notifications', label: 'Notifications', icon: <FiNotifications className="w-5 h-5" /> },
        { id: 'email-templates', to: '/admin/email-templates', label: 'Templates emails', icon: <FiEmailTemplates className="w-5 h-5" /> },
      ]
    },
    {
      id: 'settings',
      label: 'Référence',
      items: [
        { id: 'features', to: '/admin/features', label: 'Fonctionnalités', icon: <FiGrid className="w-5 h-5" /> },
    { id: 'settings', to: '/admin/settings', label: 'Paramètres', icon: <FiSettings className="w-5 h-5" /> },
      ]
    },
  ];

  const handleLogout = async () => {
    try {
      await api.logout();
    } catch (error) {
      console.error('Erreur lors de la déconnexion:', error);
    } finally {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('userType');
      navigate('/login');
    }
  };

  const isActive = (path) => {
    if (path === '/admin') {
      return location.pathname === '/admin';
    }
    return location.pathname.startsWith(path);
  };

  // Demander la permission pour les notifications du navigateur
  useEffect(() => {
    if (!('Notification' in window)) {
      console.log('[Notifications] Les notifications ne sont pas supportées par ce navigateur');
      return;
    }
    
    const checkPermission = () => {
      if (Notification.permission === 'granted') {
        setNotificationPermission('granted');
        console.log('[Notifications] Permission déjà accordée');
      } else if (Notification.permission === 'denied') {
        setNotificationPermission('denied');
        console.log('[Notifications] Permission refusée');
      } else {
        // Demander la permission après un délai pour ne pas être trop intrusif
        setTimeout(() => {
          Notification.requestPermission().then(permission => {
            console.log('[Notifications] Permission demandée, résultat:', permission);
            setNotificationPermission(permission);
          }).catch(error => {
            console.error('[Notifications] Erreur lors de la demande de permission:', error);
            setNotificationPermission('denied');
          });
        }, 2000);
      }
    };
    
    checkPermission();
  }, []);

  // Fonction pour jouer une sonnerie de notification
  const playNotificationSound = useCallback(() => {
    try {
      // Créer un contexte audio
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      
      // Résumer l'AudioContext si il est suspendu (requis par les navigateurs modernes)
      const playSound = () => {
        // Créer un oscillateur pour générer un son
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        
        // Connecter l'oscillateur au gain node puis à la sortie
        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        
        // Configurer le son (fréquence, type d'onde)
        oscillator.frequency.value = 800; // Fréquence en Hz (son aigu)
        oscillator.type = 'sine'; // Type d'onde (sine = doux)
        
        // Configurer le volume (gain)
        gainNode.gain.setValueAtTime(0.3, audioContext.currentTime); // Volume à 30%
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3); // Fade out
        
        // Jouer le son
        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + 0.3); // Durée de 300ms
        
        // Jouer un deuxième bip après un court délai pour un effet "ding-dong"
        setTimeout(() => {
          const oscillator2 = audioContext.createOscillator();
          const gainNode2 = audioContext.createGain();
          
          oscillator2.connect(gainNode2);
          gainNode2.connect(audioContext.destination);
          
          oscillator2.frequency.value = 1000; // Fréquence légèrement plus haute
          oscillator2.type = 'sine';
          
          gainNode2.gain.setValueAtTime(0.3, audioContext.currentTime);
          gainNode2.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.2);
          
          oscillator2.start(audioContext.currentTime);
          oscillator2.stop(audioContext.currentTime + 0.2);
        }, 150);
      };
      
      // Si l'AudioContext est suspendu, le résumer d'abord
      if (audioContext.state === 'suspended') {
        audioContext.resume().then(() => {
          playSound();
        }).catch(err => {
          console.warn('[Notifications] Impossible de résumer AudioContext:', err);
          // Fallback vers HTML Audio
          playFallbackSound();
        });
      } else {
        playSound();
      }
      
    } catch (error) {
      console.warn('[Notifications] Impossible de jouer le son:', error);
      // Fallback : essayer avec un élément audio HTML si disponible
      playFallbackSound();
    }
    
    // Fonction de fallback avec HTML Audio
    const playFallbackSound = () => {
      try {
        const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBSuBzvLZiTYIG2m98OSfTgwOUKbk8LllHAY4kdfyzHksBSR3x/DdkEAKFF606euoVRQKRp/g8r5sIQUrgc7y2Yk2CBtpvfDkn04MDlCm5PC5ZRwGOJHX8sx5LAUkd8fw3ZBAC');
        audio.volume = 0.3;
        audio.play().catch(() => {
          // Ignorer les erreurs de lecture audio
        });
      } catch (e) {
        // Si tout échoue, ignorer silencieusement
      }
    }
  }, []);

  const pushPlatformToast = useCallback(
    (entry) => {
      if (!entry?.id) return;
      setPlatformNotifications((prev) => {
        if (prev.some((n) => n.id === entry.id)) return prev;
        return [entry, ...prev].slice(0, 20);
      });
      playNotificationSound();
    },
    [playNotificationSound]
  );

  // Fonction pour tester les notifications
  const testNotification = useCallback(() => {
    if (!('Notification' in window)) {
      alert('Les notifications ne sont pas supportées par ce navigateur');
      return;
    }
    
    if (Notification.permission !== 'granted') {
      Notification.requestPermission().then(permission => {
        if (permission === 'granted') {
          new Notification('Test de notification', {
            body: 'Si vous voyez ce message, les notifications fonctionnent !',
            icon: window.location.origin + '/favicon.ico'
          });
        } else {
          alert('Permission refusée pour les notifications');
        }
      });
    } else {
      const testNotif = new Notification('Test de notification', {
        body: 'Si vous voyez ce message, les notifications fonctionnent !',
        icon: window.location.origin + '/favicon.ico',
        requireInteraction: true
      });
      
      testNotif.onshow = () => {
        console.log('[Test] Notification de test affichée');
      };
      
      testNotif.onerror = (error) => {
        console.error('[Test] Erreur notification de test:', error);
      };
    }
  }, []);

  // Fonction pour afficher une notification du navigateur
  const showBrowserNotification = useCallback((title, message, conversationId) => {
    if (!('Notification' in window)) {
      console.log('[Notifications] Les notifications ne sont pas supportées par ce navigateur');
      return;
    }
    
    // Vérifier la permission actuelle (peut changer)
    const currentPermission = Notification.permission;
    if (currentPermission !== 'granted') {
      console.log('[Notifications] Permission non accordée:', currentPermission);
      // Essayer de redemander la permission si elle n'est pas refusée
      if (currentPermission !== 'denied') {
        Notification.requestPermission().then(permission => {
          console.log('[Notifications] Permission demandée, résultat:', permission);
          if (permission === 'granted') {
            setNotificationPermission('granted');
            // Réessayer d'afficher la notification
            setTimeout(() => showBrowserNotification(title, message, conversationId), 100);
          }
        });
      }
      return;
    }
    
    try {
      // Vérifier si la page est visible (document.hidden)
      const isPageVisible = !document.hidden;
      console.log('[Notifications] Page visible:', isPageVisible);
      
      // Ne pas fermer les notifications précédentes - laisser le navigateur les gérer
      // Le tag unique permettra de les différencier
      const uniqueTag = `message-${conversationId}-${Date.now()}-${Math.random()}`;
      
      // Créer la notification avec un tag unique et requireInteraction pour forcer l'affichage
      let notification;
      try {
        notification = new Notification(title, {
          body: message,
          icon: window.location.origin + '/favicon.ico',
          badge: window.location.origin + '/favicon.ico',
          tag: uniqueTag,
          requireInteraction: true, // Toujours forcer l'interaction pour s'assurer qu'elle s'affiche
          silent: false,
          renotify: false // Ne pas renouveler pour éviter les conflits
        });
      } catch (iconError) {
        // Si l'icône pose problème, essayer sans icône
        console.warn('[Notifications] Erreur avec l\'icône, essai sans icône:', iconError);
        notification = new Notification(title, {
          body: message,
          tag: uniqueTag,
          requireInteraction: true,
          silent: false,
          renotify: false
        });
      }
      
      let notificationShown = false;
      let notificationClosed = false;

      notification.onclick = () => {
        console.log('[Notifications] Notification cliquée');
        notificationClosed = true;
        window.focus();
        // Naviguer vers la page des messages
        navigate('/admin/messages');
        // Ne pas fermer manuellement - laisser le navigateur le faire
      };
      
      notification.onerror = (error) => {
        console.error('[Notifications] Erreur sur la notification:', error);
      };
      
      notification.onshow = () => {
        notificationShown = true;
        console.log('[Notifications] ✅ Notification AFFICHÉE avec succès - tag:', uniqueTag);
        
        // Jouer la sonnerie de notification
        playNotificationSound();
        
        // Ajouter une notification visuelle dans l'interface aussi
        setMessageNotifications((prev) => {
          const newNotif = {
            id: uniqueTag,
            title,
            message,
            kind: 'chat',
            conversationId,
            timestamp: Date.now(),
            read: false,
          };
          const updated = [newNotif, ...prev].slice(0, 20);
          return updated;
        });
      };
      
      notification.onclose = () => {
        if (!notificationClosed) {
          console.warn('[Notifications] ⚠️ Notification fermée automatiquement (peut-être par le navigateur)');
          // Si la notification se ferme trop vite, afficher quand même dans l'interface
          if (!notificationShown) {
            // Jouer la sonnerie même si la notification ne s'affiche pas
            playNotificationSound();
            
            setMessageNotifications((prev) => {
              const newNotif = {
                id: uniqueTag,
                title,
                message,
                kind: 'chat',
                conversationId,
                timestamp: Date.now(),
                read: false,
              };
              return [newNotif, ...prev].slice(0, 20);
            });
          }
        } else {
          console.log('[Notifications] Notification fermée par l\'utilisateur');
        }
        // Ne pas retirer de l'interface - laisser l'utilisateur le faire manuellement
      };
      
      // Stocker la notification pour éviter le garbage collection
      if (!window.activeNotifications) {
        window.activeNotifications = [];
      }
      window.activeNotifications.push(notification);
      
      // Nettoyer les anciennes notifications après 30 secondes
      setTimeout(() => {
        if (window.activeNotifications) {
          window.activeNotifications = window.activeNotifications.filter(n => n !== notification);
        }
        // Ne pas fermer automatiquement - laisser l'utilisateur ou le système le faire
      }, 30000);
      
      console.log('[Notifications] Notification créée:', { 
        title, 
        message, 
        permission: Notification.permission,
        conversationId,
        isPageVisible,
        requireInteraction: true,
        uniqueTag,
        userAgent: navigator.userAgent
      });
      
      // Vérifier après 2 secondes si la notification s'est bien affichée
      setTimeout(() => {
        if (!notificationShown) {
          console.warn('[Notifications] ⚠️ La notification n\'a pas déclenché onshow après 2 secondes');
        }
      }, 2000);
      
      // Note: La vibration nécessite une interaction utilisateur, donc on ne l'utilise pas ici
      // Les notifications du navigateur devraient suffire
      
    } catch (error) {
      console.error('[Notifications] Erreur lors de l\'affichage de la notification:', error);
      // Essayer une notification très simple en cas d'erreur
      try {
        const simpleNotification = new Notification(title, {
          body: message,
          requireInteraction: true
        });
        simpleNotification.onclick = () => {
          window.focus();
          navigate('/admin/messages');
        };
        console.log('[Notifications] Notification simple créée en fallback');
      } catch (simpleError) {
        console.error('[Notifications] Impossible d\'afficher même une notification simple:', simpleError);
      }
    }
  }, [navigate, playNotificationSound]);

  /** Alerte admin : nouveau déménageur à valider (temps réel). */
  const showMoverRegistrationPendingNotification = useCallback(
    (title, message, moverId, notificationId) => {
      const uniqueTag = `mover-reg-${notificationId || moverId || Date.now()}`;
      const navigateTarget = moverId
        ? `/admin/mover-registration/${moverId}`
        : '/admin/account-verification';

      const pushPending = () => {
        setPlatformNotifications((prev) =>
          [
            {
              id: uniqueTag,
              title,
              message,
              kind: 'mover_registration',
              moverId: moverId || null,
              timestamp: Date.now(),
              read: false,
            },
            ...prev,
          ].slice(0, 20)
        );
      };

      playNotificationSound();

      if (!('Notification' in window)) {
        pushPending();
        return;
      }
      if (Notification.permission !== 'granted') {
        if (Notification.permission !== 'denied') {
          Notification.requestPermission().then((p) => {
            if (p === 'granted') setNotificationPermission('granted');
          });
        }
        pushPending();
        return;
      }

      try {
        const notification = new Notification(title || 'Nouvelle inscription déménageur', {
          body: message || 'Voir pour valider.',
          tag: uniqueTag,
          icon: window.location.origin + '/favicon.ico',
        });
        notification.onclick = () => {
          window.focus();
          navigate(navigateTarget);
        };
        pushPending();
      } catch (e) {
        console.warn('[Notifications] inscription déménageur:', e);
        pushPending();
      }
    },
    [navigate, playNotificationSound]
  );

  /** Alerte admin : nouvelle réclamation (temps réel). */
  const showReclamationCreatedNotification = useCallback(
    (title, message, ticketId, notificationId) => {
      const uniqueTag = `reclamation-${notificationId || ticketId || Date.now()}`;
      const navigateTarget = '/admin/tickets';

      const pushPending = () => {
        setPlatformNotifications((prev) =>
          [
            {
              id: uniqueTag,
              title,
              message,
              kind: 'reclamation',
              ticketId: ticketId || null,
              timestamp: Date.now(),
              read: false,
            },
            ...prev,
          ].slice(0, 20)
        );
      };

      playNotificationSound();

      if (!('Notification' in window)) {
        pushPending();
        return;
      }
      if (Notification.permission !== 'granted') {
        if (Notification.permission !== 'denied') {
          Notification.requestPermission().then((p) => {
            if (p === 'granted') setNotificationPermission('granted');
          });
        }
        pushPending();
        return;
      }

      try {
        const notification = new Notification(title || 'Nouvelle réclamation', {
          body: message || 'Ouvrez Tickets pour traiter.',
          tag: uniqueTag,
          icon: window.location.origin + '/favicon.ico',
        });
        notification.onclick = () => {
          window.focus();
          navigate(navigateTarget);
        };
        pushPending();
      } catch (e) {
        console.warn('[Notifications] réclamation:', e);
        pushPending();
      }
    },
    [navigate, playNotificationSound]
  );

  const showClientRegisteredNotification = useCallback(
    (title, message, clientId, notificationId) => {
      const uniqueTag = `client-reg-${notificationId || clientId || Date.now()}`;
      const navigateTarget = '/admin/users';

      const pushPending = () => {
        pushPlatformToast({
          id: uniqueTag,
          title: title || 'Nouvelle inscription client',
          message: message || 'Un client vient de créer un compte.',
          kind: 'client_registration',
          clientId: clientId || null,
          timestamp: Date.now(),
          read: false,
        });
      };

      if (!('Notification' in window)) {
        pushPending();
        return;
      }
      if (Notification.permission !== 'granted') {
        if (Notification.permission !== 'denied') {
          Notification.requestPermission().then((p) => {
            if (p === 'granted') setNotificationPermission('granted');
          });
        }
        pushPending();
        return;
      }

      try {
        const notification = new Notification(title || 'Nouvelle inscription client', {
          body: message || 'Voir Utilisateurs.',
          tag: uniqueTag,
          icon: window.location.origin + '/favicon.ico',
        });
        notification.onclick = () => {
          window.focus();
          navigate(navigateTarget);
        };
        pushPending();
      } catch (e) {
        console.warn('[Notifications] inscription client:', e);
        pushPending();
      }
    },
    [navigate, pushPlatformToast]
  );

  const showMoverAutoBannedNotification = useCallback(
    (title, message, moverId, notificationId, extra = {}) => {
      const uniqueTag = `auto-ban-${notificationId || moverId || Date.now()}`;
      const navigateTarget = '/admin/users';

      const pushPending = () => {
        pushPlatformToast({
          id: uniqueTag,
          title: title || 'Bannissement automatique',
          message:
            message ||
            'Un déménageur a été suspendu après 3 annulations de mission aujourd’hui.',
          kind: 'mover_auto_banned',
          moverId: moverId || null,
          cancellationsToday: extra.cancellationsToday,
          timestamp: Date.now(),
          read: false,
        });
      };

      if (!('Notification' in window)) {
        pushPending();
        return;
      }
      if (Notification.permission !== 'granted') {
        if (Notification.permission !== 'denied') {
          Notification.requestPermission().then((p) => {
            if (p === 'granted') setNotificationPermission('granted');
          });
        }
        pushPending();
        return;
      }

      try {
        const notification = new Notification(title || 'Bannissement automatique — déménageur', {
          body: message || 'Compte suspendu (3 annulations / jour).',
          tag: uniqueTag,
          icon: window.location.origin + '/favicon.ico',
        });
        notification.onclick = () => {
          window.focus();
          navigate(navigateTarget);
        };
        pushPending();
      } catch (e) {
        console.warn('[Notifications] auto-ban:', e);
        pushPending();
      }
    },
    [navigate, pushPlatformToast]
  );

  // Exposer la fonction de test globalement pour le débogage
  useEffect(() => {
    window.testNotification = testNotification;
    return () => {
      delete window.testNotification;
    };
  }, [testNotification]);

  useEffect(() => {
    if (!location.pathname.startsWith('/admin')) return undefined;
    socketService.connect().catch(() => {});
    const handler = (payload) => {
      if (!payload) return;
      const moverId = payload.moverId || payload.data?.moverId;
      const title = payload.title || 'Nouvelle inscription déménageur';
      const message =
        payload.message || 'Un déménageur attend validation — ouvrez Vérification comptes.';
      const nid = payload.notificationId;
      showMoverRegistrationPendingNotification(title, message, moverId, nid);
    };
    const unsub = socketService.on('mover_registration_pending', handler);
    return () => unsub?.();
  }, [location.pathname, showMoverRegistrationPendingNotification]);

  useEffect(() => {
    if (!location.pathname.startsWith('/admin')) return undefined;
    socketService.connect().catch(() => {});
    const handler = (payload) => {
      if (!payload) return;
      const ticketId = payload.ticketId;
      const title = payload.title || 'Nouvelle réclamation';
      const message =
        payload.message || 'Une nouvelle réclamation a été soumise — consultez Tickets.';
      const nid = payload.notificationId;
      showReclamationCreatedNotification(title, message, ticketId, nid);
    };
    const unsub = socketService.on('reclamation_created', handler);
    return () => unsub?.();
  }, [location.pathname, showReclamationCreatedNotification]);

  useEffect(() => {
    if (!location.pathname.startsWith('/admin')) return undefined;
    socketService.connect().catch(() => {});
    const handler = (payload) => {
      if (!payload) return;
      const clientId = payload.clientId || payload.data?.clientId;
      showClientRegisteredNotification(
        payload.title || 'Nouvelle inscription client',
        payload.message || 'Un client vient de s’inscrire.',
        clientId,
        payload.notificationId
      );
    };
    const unsub = socketService.on('client_registration_pending', handler);
    return () => unsub?.();
  }, [location.pathname, showClientRegisteredNotification]);

  useEffect(() => {
    if (!location.pathname.startsWith('/admin')) return undefined;
    socketService.connect().catch(() => {});
    const handler = (payload) => {
      if (!payload) return;
      const moverId = payload.moverId || payload.data?.moverId;
      showMoverAutoBannedNotification(
        payload.title || 'Bannissement automatique — déménageur',
        payload.message ||
          'Compte suspendu : 3 annulations ou refus de mission dans la journée.',
        moverId,
        payload.notificationId,
        { cancellationsToday: payload.cancellationsToday || payload.data?.cancellationsToday }
      );
    };
    const unsub = socketService.on('mover_auto_banned', handler);
    return () => unsub?.();
  }, [location.pathname, showMoverAutoBannedNotification]);

  const showContentReportNotification = useCallback(
    (title, message, reportId) => {
      const uniqueTag = `content-report-${reportId || Date.now()}`;
      const navigateTarget = '/admin/moderation';
      const pushPending = () => {
        pushPlatformToast({
          id: uniqueTag,
          title: title || 'Nouveau signalement',
          message: message || 'Ouvrez Modération pour traiter.',
          kind: 'content_report',
          reportId: reportId || null,
          timestamp: Date.now(),
          read: false,
        });
      };
      if (!('Notification' in window)) {
        pushPending();
        return;
      }
      if (Notification.permission !== 'granted') {
        if (Notification.permission !== 'denied') {
          Notification.requestPermission().then((p) => {
            if (p === 'granted') setNotificationPermission('granted');
          });
        }
        pushPending();
        return;
      }
      try {
        const notification = new Notification(title || 'Nouveau signalement', {
          body: message || 'Ouvrez Modération pour traiter.',
          tag: uniqueTag,
          icon: window.location.origin + '/favicon.ico',
        });
        notification.onclick = () => {
          window.focus();
          navigate(navigateTarget);
        };
        pushPending();
      } catch (e) {
        console.warn('[Notifications] signalement:', e);
        pushPending();
      }
    },
    [navigate, pushPlatformToast]
  );

  useEffect(() => {
    if (!location.pathname.startsWith('/admin')) return undefined;
    socketService.connect().catch(() => {});
    const onReport = (payload) => {
      if (!payload) return;
      showContentReportNotification(
        'Nouveau signalement',
        'Un utilisateur a signalé du contenu — ouvrez Modération.',
        payload.reportId
      );
    };
    const onNotif = (n) => {
      if (!n?.type) return;
      const data = n.data || {};
      const nid = n.id || n.notificationId;
      switch (n.type) {
        case 'content_report':
          showContentReportNotification(n.title, n.message, data.reportId || nid);
          break;
        case 'client_registration_pending':
          showClientRegisteredNotification(n.title, n.message, data.clientId, nid);
          break;
        case 'mover_auto_banned':
          showMoverAutoBannedNotification(n.title, n.message, data.moverId, nid, data);
          break;
        case 'mover_registration_pending':
          showMoverRegistrationPendingNotification(n.title, n.message, data.moverId, nid);
          break;
        case 'reclamation_created':
          showReclamationCreatedNotification(n.title, n.message, data.ticketId, nid);
          break;
        default:
          break;
      }
    };
    const unsub1 = socketService.on('content_report', onReport);
    const unsub2 = socketService.on('notification', onNotif);
    return () => {
      unsub1?.();
      unsub2?.();
    };
  }, [
    location.pathname,
    showContentReportNotification,
    showClientRegisteredNotification,
    showMoverAutoBannedNotification,
    showMoverRegistrationPendingNotification,
    showReclamationCreatedNotification,
  ]);

  // Pop-up immédiat à la réception d’un message client / déménageur (Socket)
  useEffect(() => {
    if (!location.pathname.startsWith('/admin')) return undefined;
    const adminId = user?.id;
    if (!adminId) return undefined;

    const formatPreview = (msg) => {
      const type = msg?.messageType || msg?.message_type;
      const content = (msg?.content || '').toString();
      if (content) return content.length > 100 ? `${content.slice(0, 100)}…` : content;
      if (type === 'voice') return '🎤 Message vocal';
      if (type === 'image') return '📷 Photo';
      if (type === 'document') return '📄 Document';
      if (type === 'location') return '📍 Localisation';
      return 'Nouveau message';
    };

    const roleSuffix = (role) => {
      if (role === 'demenageur' || role === 'mover') return ' — déménageur';
      if (role === 'client') return ' — client';
      return '';
    };

    socketService.connect().catch(() => {});

    const onNewMessageRealtime = (payload) => {
      if (!payload) return;
      const msg = payload.message || payload;
      const senderId = msg?.senderId ?? msg?.sender_id;
      if (!senderId || String(senderId) === String(adminId)) return;

      const messageId = msg?.id || msg?._id;
      const convKey = payload.chatId || payload.chat_id || 'msg';
      if (messageId) {
        const prev = lastMessageIdsRef.current.get(convKey);
        if (prev && String(prev) === String(messageId)) return;
        lastMessageIdsRef.current.set(convKey, messageId);
      }

      const senderName = msg?.senderName || msg?.sender_name || 'Utilisateur';
      const senderRole = msg?.senderType || msg?.sender_type || '';
      const title = `Message${roleSuffix(senderRole)} : ${senderName}`;
      showBrowserNotification(title, formatPreview(msg), payload.chatId || convKey);
    };

    const unsub = socketService.on('new_message', onNewMessageRealtime);
    return () => unsub?.();
  }, [location.pathname, user?.id, showBrowserNotification]);

  // Temps réel : pas de polling périodique. Sync API au montage, puis sur événements socket / fenêtre.
  useEffect(() => {
    let debounceTimer = null;
    const pollMessages = async () => {
      try {
        const conversationsResponse = await api.getAdminConversations();

        if (!conversationsResponse?.success || !Array.isArray(conversationsResponse.data)) {
          return;
        }

        const conversations = conversationsResponse.data;
        conversationsRef.current = conversations;

        const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
        if (!currentUser?.id) return;

        const skipNotifyThisRound = firstConversationPollRef.current;
        if (firstConversationPollRef.current) {
          firstConversationPollRef.current = false;
        }

        for (const conv of conversations) {
          if (!conv?.userId) continue;

          const lastMessageId =
            conv.lastMessageId || conv.last_message_id || null;
          const senderId =
            conv.lastMessageSenderId ||
            conv.last_message_sender_id ||
            null;
          if (!lastMessageId) continue;

          const storedLastId = lastMessageIdsRef.current.get(conv.id);
          if (lastMessageId === storedLastId) continue;

          lastMessageIdsRef.current.set(conv.id, lastMessageId);

          const isFromAdmin = senderId && String(senderId) === String(currentUser.id);
          if (!skipNotifyThisRound && !isFromAdmin) {
            const messagePreview = (conv.lastMessage || conv.last_message || '')
              .toString()
              .substring(0, 100) || 'Nouveau message';
            const userName = conv.userName || conv.user_name || 'Utilisateur';
            showBrowserNotification(
              `Nouveau message de ${userName}`,
              messagePreview,
              conv.id
            );
          }
        }
      } catch (error) {
        console.error('[Notifications] Erreur lors du polling des conversations:', error);
      }
    };

    const schedulePoll = () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => pollMessages(), 400);
    };

    if (!location.pathname.startsWith('/admin')) {
      return undefined;
    }

    pollMessages();
    socketService.connect().catch(() => {});
    const unsubNewMessage = socketService.on('new_message', schedulePoll);
    const unsubMessageSent = socketService.on('message_sent', schedulePoll);
    const unsubChatCreated = socketService.on('chat_created', schedulePoll);
    const onFocus = () => schedulePoll();
    const onVisibility = () => {
      if (!document.hidden) schedulePoll();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      unsubNewMessage?.();
      unsubMessageSent?.();
      unsubChatCreated?.();
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [notificationPermission, showBrowserNotification, location.pathname]);

  // Fermer le dropdown si on clique en dehors
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationDropdownRef.current && !notificationDropdownRef.current.contains(event.target)) {
        setShowNotificationDropdown(false);
      }
    };

    if (showNotificationDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showNotificationDropdown]);

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Toasts : plateforme (réclamations, inscriptions) et messages — listes séparées */}
      {(platformNotifications.length > 0 || messageNotifications.length > 0) && (
        <div className="fixed top-20 right-4 z-50 flex flex-col gap-3 max-w-md">
          {platformNotifications.length > 0 && (
            <div className="space-y-2">
              {platformNotifications.map((notif) => (
                <div
                  key={notif.id}
                  onClick={() => {
                    navigateForPlatformKind(notif.kind, notif);
                    setPlatformNotifications((prev) => prev.filter((n) => n.id !== notif.id));
                  }}
                  className="bg-gradient-to-r from-amber-700 to-amber-600 text-white rounded-xl shadow-2xl p-4 min-w-[320px] max-w-md border-2 border-amber-400/40 cursor-pointer hover:scale-[1.02] transition-transform animate-slide-in-right"
                  style={{ animation: 'slideInRight 0.3s ease-out' }}
                >
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 bg-white/15 rounded-full flex items-center justify-center">
                        {notif.kind === 'reclamation' ? (
                          <FiHelpCircle className="w-5 h-5 text-white" />
                        ) : notif.kind === 'client_registration' ? (
                          <FiUserPlus className="w-5 h-5 text-white" />
                        ) : notif.kind === 'mover_auto_banned' ? (
                          <FiAlertOctagon className="w-5 h-5 text-white" />
                        ) : notif.kind === 'content_report' ? (
                          <FiShield className="w-5 h-5 text-white" />
                        ) : (
                          <FiUserCheck className="w-5 h-5 text-white" />
                        )}
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] font-semibold uppercase tracking-wide text-amber-200/90 mb-0.5">
                        Plateforme
                      </p>
                      <h4 className="font-bold text-sm mb-1">{notif.title}</h4>
                      <p className="text-xs text-amber-50/95 line-clamp-2">{notif.message}</p>
                      <p className="mt-2 text-xs font-semibold text-amber-100">Cliquez pour ouvrir</p>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setPlatformNotifications((prev) => prev.filter((n) => n.id !== notif.id));
                      }}
                      className="flex-shrink-0 text-white/80 hover:text-white"
                    >
                      <FiX className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
          {messageNotifications.length > 0 && (
            <div className="space-y-2">
              {messageNotifications.map((notif) => (
                <div
                  key={notif.id}
                  onClick={() => {
                    navigate('/admin/messages');
                    setMessageNotifications((prev) => prev.filter((n) => n.id !== notif.id));
                  }}
                  className="bg-gradient-to-r from-yellow-600 to-yellow-500 text-gray-900 rounded-xl shadow-2xl p-4 min-w-[320px] max-w-md border-2 border-yellow-400/50 cursor-pointer hover:scale-[1.02] transition-transform animate-slide-in-right"
                  style={{ animation: 'slideInRight 0.3s ease-out' }}
                >
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 bg-yellow-400/30 rounded-full flex items-center justify-center">
                        <FiMessageSquare className="w-5 h-5 text-yellow-900" />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] font-semibold uppercase tracking-wide text-yellow-900/80 mb-0.5">
                        Messages
                      </p>
                      <h4 className="font-bold text-sm mb-1">{notif.title}</h4>
                      <p className="text-xs text-gray-800 line-clamp-2">{notif.message}</p>
                      <p className="mt-2 text-xs font-semibold text-yellow-900">Cliquez pour ouvrir</p>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setMessageNotifications((prev) => prev.filter((n) => n.id !== notif.id));
                      }}
                      className="flex-shrink-0 text-gray-800 hover:text-gray-900"
                    >
                      <FiX className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      
      {/* Style pour l'animation */}
      <style>{`
        @keyframes slideInRight {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `}</style>
      {/* Sidebar Desktop */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-72 bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900 border-r border-yellow-600/20 shadow-2xl transform transition-all duration-500 ease-out flex flex-col ${
        isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
      } lg:translate-x-0`}>
        {/* Logo Header - Enhanced */}
        <div className="relative h-24 px-6 bg-gradient-to-r from-gray-900 to-gray-800 border-b border-yellow-600/30 flex-shrink-0">
          <div className="relative flex items-center justify-between h-full">
            <div className="flex items-center space-x-3">
              <div className="relative">
                <div className="w-14 h-14 bg-gradient-to-br from-yellow-500/30 to-yellow-600/20 border-2 border-yellow-500/40 rounded-xl flex items-center justify-center shadow-lg shadow-yellow-500/20">
                  <span className="text-yellow-400 font-extrabold text-2xl">L</span>
                </div>
                <div className="absolute -top-1 -right-1 w-4 h-4 bg-yellow-500 rounded-full border-2 border-gray-900 shadow-lg"></div>
              </div>
              <div>
                <h1 className="text-yellow-400 text-xl font-extrabold tracking-tight bg-gradient-to-r from-yellow-400 to-yellow-300 bg-clip-text text-transparent">
                  Liberty
                </h1>
                <p className="text-yellow-500/60 text-xs font-semibold uppercase tracking-wider">Admin Panel</p>
              </div>
            </div>
            <button
              onClick={() => setIsSidebarOpen(false)}
              className="lg:hidden text-gray-400 hover:text-yellow-400 hover:bg-gray-700/50 rounded-lg p-2 transition-all"
            >
              <FiX className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Navigation - scrollable avec groupes */}
        <nav className="flex-1 overflow-y-auto mt-6 px-4 pb-4 space-y-6 scrollbar-thin scrollbar-thumb-yellow-600/20 scrollbar-track-transparent">
          {menuGroups.map((group, groupIndex) => (
            <div key={group.id} className="space-y-2">
              {/* Titre du groupe (seulement si défini) */}
              {group.label && (
                <div className="px-3 mb-3 mt-4 first:mt-0">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500/80 flex items-center">
                    <span className="h-px flex-1 bg-gradient-to-r from-transparent via-yellow-600/30 to-transparent mr-3"></span>
                    {group.label}
                    <span className="h-px flex-1 bg-gradient-to-r from-transparent via-yellow-600/30 to-transparent ml-3"></span>
                  </h3>
                </div>
              )}
              
              {/* Items du groupe */}
              <div className="space-y-1.5">
                {group.items.map((item) => {
            const active = isActive(item.to);
            return (
              <NavLink
                key={item.id}
                to={item.to}
                      className={`group relative flex items-center px-4 py-3 text-sm font-semibold rounded-xl transition-all duration-300 ${
                  active
                          ? 'bg-gradient-to-r from-yellow-600/25 to-yellow-500/15 text-yellow-400 border border-yellow-500/40 shadow-lg shadow-yellow-500/10'
                          : 'text-gray-300 hover:bg-gray-700/50 hover:text-yellow-400 border border-transparent hover:border-yellow-600/20 hover:shadow-md'
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                      {/* Indicateur actif gauche */}
                {active && (
                        <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1.5 h-10 bg-gradient-to-b from-yellow-400 to-yellow-600 rounded-r-full shadow-lg shadow-yellow-500/50"></div>
                      )}
                      
                      {/* Icône avec animation */}
                      <span className={`mr-3 transition-all duration-300 ${
                        active 
                          ? 'scale-110 text-yellow-400' 
                          : 'group-hover:scale-110 group-hover:text-yellow-400 text-gray-400'
                      }`}>
                  {item.icon}
                </span>
                      
                      {/* Label */}
                      <span className={`flex-1 transition-colors duration-200 ${
                        active ? 'text-yellow-400' : 'text-gray-300 group-hover:text-yellow-400'
                      }`}>
                        {item.label}
                      </span>
                      
                      {/* Chevron pour élément actif */}
                {active && (
                        <FiChevronRight className="w-4 h-4 text-yellow-400/60 animate-pulse" />
                      )}
                      
                      {/* Effet de brillance au hover */}
                      {!active && (
                        <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-transparent via-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                )}
              </NavLink>
            );
          })}
              </div>
            </div>
          ))}
        </nav>

        {/* Footer avec profil utilisateur - Enhanced */}
        <div className="flex-shrink-0 w-full p-4 bg-gradient-to-t from-gray-900/95 to-gray-800/95 border-t border-yellow-600/20 backdrop-blur-sm">
          {/* Profil utilisateur */}
          <div className="flex items-center mb-3 px-3 py-3 rounded-xl bg-gradient-to-r from-gray-700/60 to-gray-800/60 border border-gray-600/50 hover:border-yellow-600/30 transition-all duration-300 hover:shadow-lg hover:shadow-yellow-500/10">
            <div className="relative">
              <div className="w-12 h-12 bg-gradient-to-br from-yellow-500/30 to-yellow-600/20 border-2 border-yellow-500/40 rounded-xl flex items-center justify-center shadow-md">
                <FiUser className="w-6 h-6 text-yellow-400" />
              </div>
              <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 bg-green-500 rounded-full border-2 border-gray-900 shadow-lg"></div>
            </div>
            <div className="ml-3 flex-1 min-w-0">
              <p className="text-sm font-bold text-white truncate">
                {user.first_name || 'Admin'} {user.last_name || ''}
              </p>
              <p className="text-xs text-gray-400 truncate font-medium">{user.email || 'admin@liberty.com'}</p>
            </div>
          </div>
          
          {/* Bouton déconnexion */}
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center px-4 py-3 text-sm font-bold text-white bg-gradient-to-r from-red-600/90 to-red-700/90 hover:from-red-600 hover:to-red-700 rounded-xl transition-all duration-300 border border-red-600/50 hover:border-red-500 shadow-lg hover:shadow-red-500/20 hover:scale-[1.02] active:scale-[0.98]"
          >
            <FiLogOut className="mr-2 w-4 h-4" />
            Déconnexion
          </button>
        </div>
      </aside>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-40 lg:hidden transition-opacity duration-300"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Main Content */}
      <div className={`transition-all duration-500 ease-out ${isSidebarOpen ? 'lg:ml-72' : 'lg:ml-0'}`}>
        {/* Top Bar */}
        <header className="sticky top-0 z-30 bg-gray-800 border-b border-yellow-600/30 shadow-lg">
          <div className="flex items-center justify-between h-20 px-4 lg:px-8">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2.5 rounded-lg text-gray-300 hover:bg-gray-700 hover:text-yellow-400 transition-all"
            >
              <FiMenu className="w-6 h-6" />
            </button>
            <div className="flex-1" />
            <div className="flex items-center space-x-3">
              <div className="relative" ref={notificationDropdownRef}>
                <button 
                  onClick={() => setShowNotificationDropdown(!showNotificationDropdown)}
                  className="relative p-2.5 rounded-lg text-gray-300 hover:bg-gray-700 hover:text-yellow-400 transition-all group"
                >
                <FiBell className="w-5 h-5" />
                  {(() => {
                    const unreadTotal =
                      platformNotifications.filter((n) => !n.read).length +
                      messageNotifications.filter((n) => !n.read).length;
                    return unreadTotal > 0 && (
                      <span className="absolute top-1.5 right-1.5 w-5 h-5 bg-yellow-500 rounded-full ring-2 ring-gray-800 animate-pulse flex items-center justify-center">
                        <span className="text-xs font-bold text-gray-900">
                          {unreadTotal > 9 ? '9+' : unreadTotal}
                        </span>
                      </span>
                    );
                  })()}
                </button>
                
                {/* Dropdown des notifications */}
                {showNotificationDropdown && (
                  <div className="absolute right-0 mt-2 w-80 bg-gray-800 border border-yellow-600/30 rounded-xl shadow-2xl z-50 max-h-96 overflow-hidden flex flex-col">
                    {/* Header */}
                    <div className="p-4 border-b border-yellow-600/30 bg-gray-900">
                      <div className="flex items-center justify-between">
                        <h3 className="text-lg font-bold text-yellow-400 flex items-center">
                          <FiBell className="mr-2" />
                          Notifications
                          {(() => {
                            const unreadTotal =
                              platformNotifications.filter((n) => !n.read).length +
                              messageNotifications.filter((n) => !n.read).length;
                            return unreadTotal > 0 && (
                              <span className="ml-2 px-2 py-0.5 bg-yellow-600 text-gray-900 text-xs font-bold rounded-full">
                                {unreadTotal}
                              </span>
                            );
                          })()}
                        </h3>
                        {(platformNotifications.length > 0 || messageNotifications.length > 0) && (
                          <button
                            type="button"
                            onClick={() => {
                              setPlatformNotifications([]);
                              setMessageNotifications([]);
                            }}
                            className="text-xs text-gray-400 hover:text-yellow-400 transition-colors"
                          >
                            Tout effacer
                          </button>
                        )}
                      </div>
                    </div>
                    
                    {/* Liste des notifications */}
                    <div className="overflow-y-auto flex-1">
                      {platformNotifications.length === 0 && messageNotifications.length === 0 ? (
                        <div className="p-8 text-center">
                          <FiBell className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                          <p className="text-gray-400 text-sm">Aucune notification</p>
                        </div>
                      ) : (
                        <div className="divide-y divide-yellow-600/10">
                          {platformNotifications.length > 0 && (
                            <>
                              <div className="px-4 py-2 text-[11px] font-bold uppercase tracking-wider text-amber-400/90 bg-gray-900/90 sticky top-0 z-[1]">
                                Plateforme
                              </div>
                              {platformNotifications.map((notif) => (
                                <div
                                  key={notif.id}
                                  onClick={() => {
                                    navigateForPlatformKind(notif.kind, notif);
                                    setShowNotificationDropdown(false);
                                    setPlatformNotifications((prev) =>
                                      prev.map((n) => (n.id === notif.id ? { ...n, read: true } : n))
                                    );
                                  }}
                                  className={`p-4 hover:bg-gray-700/50 cursor-pointer transition-colors group ${
                                    !notif.read ? 'bg-amber-900/10 border-l-2 border-amber-500' : ''
                                  }`}
                                >
                                  <div className="flex items-start space-x-3">
                                    <div className="flex-shrink-0 mt-1 relative">
                                      <div
                                        className={`w-10 h-10 rounded-full flex items-center justify-center border ${
                                          !notif.read
                                            ? 'bg-amber-600/25 border-amber-500/50'
                                            : 'bg-gray-700/50 border-gray-600/30'
                                        }`}
                                      >
                                        {notif.kind === 'reclamation' ? (
                                          <FiHelpCircle
                                            className={`w-5 h-5 ${
                                              !notif.read ? 'text-amber-300' : 'text-gray-400'
                                            }`}
                                          />
                                        ) : notif.kind === 'client_registration' ? (
                                          <FiUserPlus
                                            className={`w-5 h-5 ${
                                              !notif.read ? 'text-amber-300' : 'text-gray-400'
                                            }`}
                                          />
                                        ) : notif.kind === 'mover_auto_banned' ? (
                                          <FiAlertOctagon
                                            className={`w-5 h-5 ${
                                              !notif.read ? 'text-amber-300' : 'text-gray-400'
                                            }`}
                                          />
                                        ) : notif.kind === 'content_report' ? (
                                          <FiShield
                                            className={`w-5 h-5 ${
                                              !notif.read ? 'text-amber-300' : 'text-gray-400'
                                            }`}
                                          />
                                        ) : (
                                          <FiUserCheck
                                            className={`w-5 h-5 ${
                                              !notif.read ? 'text-amber-300' : 'text-gray-400'
                                            }`}
                                          />
                                        )}
                                      </div>
                                      {!notif.read && (
                                        <div className="absolute -top-0.5 -right-0.5 w-3 h-3 bg-amber-500 rounded-full border-2 border-gray-800" />
                                      )}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center justify-between mb-1">
                                        <h4
                                          className={`font-semibold text-sm group-hover:text-amber-300 transition-colors ${
                                            !notif.read ? 'text-white' : 'text-gray-300'
                                          }`}
                                        >
                                          {notif.title}
                                        </h4>
                                        {!notif.read && (
                                          <span className="w-2 h-2 bg-amber-500 rounded-full" />
                                        )}
                                      </div>
                                      <p className="text-xs text-gray-400 line-clamp-2 mb-2">{notif.message}</p>
                                      <p className="text-xs text-gray-500">
                                        {new Date(notif.timestamp).toLocaleTimeString('fr-FR', {
                                          hour: '2-digit',
                                          minute: '2-digit',
                                        })}
                                      </p>
                                    </div>
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setPlatformNotifications((prev) => prev.filter((n) => n.id !== notif.id));
                                      }}
                                      className="flex-shrink-0 text-gray-500 hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100"
                                    >
                                      <FiX className="w-4 h-4" />
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </>
                          )}
                          {messageNotifications.length > 0 && (
                            <>
                              <div className="px-4 py-2 text-[11px] font-bold uppercase tracking-wider text-yellow-500/90 bg-gray-900/90 sticky top-0 z-[1] border-t border-yellow-600/20">
                                Messages
                              </div>
                              {messageNotifications.map((notif) => (
                                <div
                                  key={notif.id}
                                  onClick={() => {
                                    navigate('/admin/messages');
                                    setShowNotificationDropdown(false);
                                    setMessageNotifications((prev) =>
                                      prev.map((n) => (n.id === notif.id ? { ...n, read: true } : n))
                                    );
                                  }}
                                  className={`p-4 hover:bg-gray-700/50 cursor-pointer transition-colors group ${
                                    !notif.read ? 'bg-yellow-600/5 border-l-2 border-yellow-500' : ''
                                  }`}
                                >
                                  <div className="flex items-start space-x-3">
                                    <div className="flex-shrink-0 mt-1 relative">
                                      <div
                                        className={`w-10 h-10 rounded-full flex items-center justify-center border ${
                                          !notif.read
                                            ? 'bg-yellow-600/30 border-yellow-500/50'
                                            : 'bg-gray-700/50 border-gray-600/30'
                                        }`}
                                      >
                                        <FiMessageSquare
                                          className={`w-5 h-5 ${
                                            !notif.read ? 'text-yellow-400' : 'text-gray-400'
                                          }`}
                                        />
                                      </div>
                                      {!notif.read && (
                                        <div className="absolute -top-0.5 -right-0.5 w-3 h-3 bg-yellow-500 rounded-full border-2 border-gray-800" />
                                      )}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center justify-between mb-1">
                                        <h4
                                          className={`font-semibold text-sm group-hover:text-yellow-400 transition-colors ${
                                            !notif.read ? 'text-white' : 'text-gray-300'
                                          }`}
                                        >
                                          {notif.title}
                                        </h4>
                                        {!notif.read && (
                                          <span className="w-2 h-2 bg-yellow-500 rounded-full" />
                                        )}
                                      </div>
                                      <p className="text-xs text-gray-400 line-clamp-2 mb-2">{notif.message}</p>
                                      <p className="text-xs text-gray-500">
                                        {new Date(notif.timestamp).toLocaleTimeString('fr-FR', {
                                          hour: '2-digit',
                                          minute: '2-digit',
                                        })}
                                      </p>
                                    </div>
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setMessageNotifications((prev) => prev.filter((n) => n.id !== notif.id));
                                      }}
                                      className="flex-shrink-0 text-gray-500 hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100"
                                    >
                                      <FiX className="w-4 h-4" />
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              <div className="hidden md:flex items-center space-x-3 px-4 py-2 rounded-lg bg-gray-700/50 border border-gray-700">
                <div className="w-10 h-10 bg-yellow-600/20 border border-yellow-600/50 rounded-lg flex items-center justify-center">
                  <FiUser className="w-5 h-5 text-yellow-400" />
                </div>
                <div className="text-sm">
                  <p className="font-semibold text-white">{user.first_name || 'Admin'}</p>
                  <p className="text-xs text-gray-400">Administrateur</p>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-4 lg:p-8 bg-gray-900 min-h-screen">
          <Outlet />
        </main>
      </div>

      {/* Mobile Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-72 bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900 border-r border-yellow-600/20 shadow-2xl transform transition-all duration-500 ease-out lg:hidden flex flex-col ${
          isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* Logo Header - Enhanced */}
        <div className="relative h-24 px-6 bg-gradient-to-r from-gray-900 to-gray-800 border-b border-yellow-600/30 flex-shrink-0">
          <div className="relative flex items-center justify-between h-full">
            <div className="flex items-center space-x-3">
              <div className="relative">
                <div className="w-14 h-14 bg-gradient-to-br from-yellow-500/30 to-yellow-600/20 border-2 border-yellow-500/40 rounded-xl flex items-center justify-center shadow-lg shadow-yellow-500/20">
                  <span className="text-yellow-400 font-extrabold text-2xl">L</span>
                </div>
                <div className="absolute -top-1 -right-1 w-4 h-4 bg-yellow-500 rounded-full border-2 border-gray-900 shadow-lg"></div>
              </div>
              <div>
                <h1 className="text-yellow-400 text-xl font-extrabold tracking-tight bg-gradient-to-r from-yellow-400 to-yellow-300 bg-clip-text text-transparent">
                  Liberty
                </h1>
                <p className="text-yellow-500/60 text-xs font-semibold uppercase tracking-wider">Admin Panel</p>
              </div>
            </div>
            <button
              onClick={() => setIsMobileMenuOpen(false)}
              className="text-gray-400 hover:text-yellow-400 hover:bg-gray-700/50 rounded-lg p-2 transition-all"
            >
              <FiX className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Navigation - scrollable avec groupes */}
        <nav className="flex-1 overflow-y-auto mt-6 px-4 pb-4 space-y-6 scrollbar-thin scrollbar-thumb-yellow-600/20 scrollbar-track-transparent">
          {menuGroups.map((group, groupIndex) => (
            <div key={group.id} className="space-y-2">
              {/* Titre du groupe (seulement si défini) */}
              {group.label && (
                <div className="px-3 mb-3 mt-4 first:mt-0">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500/80 flex items-center">
                    <span className="h-px flex-1 bg-gradient-to-r from-transparent via-yellow-600/30 to-transparent mr-3"></span>
                    {group.label}
                    <span className="h-px flex-1 bg-gradient-to-r from-transparent via-yellow-600/30 to-transparent ml-3"></span>
                  </h3>
                </div>
              )}
              
              {/* Items du groupe */}
              <div className="space-y-1.5">
                {group.items.map((item) => {
            const active = isActive(item.to);
            return (
              <NavLink
                key={item.id}
                to={item.to}
                      className={`group relative flex items-center px-4 py-3 text-sm font-semibold rounded-xl transition-all duration-300 ${
                  active
                          ? 'bg-gradient-to-r from-yellow-600/25 to-yellow-500/15 text-yellow-400 border border-yellow-500/40 shadow-lg shadow-yellow-500/10'
                          : 'text-gray-300 hover:bg-gray-700/50 hover:text-yellow-400 border border-transparent hover:border-yellow-600/20 hover:shadow-md'
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                      {/* Indicateur actif gauche */}
                {active && (
                        <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1.5 h-10 bg-gradient-to-b from-yellow-400 to-yellow-600 rounded-r-full shadow-lg shadow-yellow-500/50"></div>
                      )}
                      
                      {/* Icône avec animation */}
                      <span className={`mr-3 transition-all duration-300 ${
                        active 
                          ? 'scale-110 text-yellow-400' 
                          : 'group-hover:scale-110 group-hover:text-yellow-400 text-gray-400'
                      }`}>
                  {item.icon}
                </span>
                      
                      {/* Label */}
                      <span className={`flex-1 transition-colors duration-200 ${
                        active ? 'text-yellow-400' : 'text-gray-300 group-hover:text-yellow-400'
                      }`}>
                        {item.label}
                      </span>
                      
                      {/* Chevron pour élément actif */}
                {active && (
                        <FiChevronRight className="w-4 h-4 text-yellow-400/60 animate-pulse" />
                      )}
                      
                      {/* Effet de brillance au hover */}
                      {!active && (
                        <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-transparent via-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                )}
              </NavLink>
            );
          })}
              </div>
            </div>
          ))}
        </nav>

        {/* Footer avec profil utilisateur - Enhanced */}
        <div className="flex-shrink-0 w-full p-4 bg-gradient-to-t from-gray-900/95 to-gray-800/95 border-t border-yellow-600/20 backdrop-blur-sm">
          {/* Profil utilisateur */}
          <div className="flex items-center mb-3 px-3 py-3 rounded-xl bg-gradient-to-r from-gray-700/60 to-gray-800/60 border border-gray-600/50 hover:border-yellow-600/30 transition-all duration-300 hover:shadow-lg hover:shadow-yellow-500/10">
            <div className="relative">
              <div className="w-12 h-12 bg-gradient-to-br from-yellow-500/30 to-yellow-600/20 border-2 border-yellow-500/40 rounded-xl flex items-center justify-center shadow-md">
                <FiUser className="w-6 h-6 text-yellow-400" />
              </div>
              <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 bg-green-500 rounded-full border-2 border-gray-900 shadow-lg"></div>
            </div>
            <div className="ml-3 flex-1 min-w-0">
              <p className="text-sm font-bold text-white truncate">
                {user.first_name || 'Admin'} {user.last_name || ''}
              </p>
              <p className="text-xs text-gray-400 truncate font-medium">{user.email || 'admin@liberty.com'}</p>
            </div>
          </div>
          
          {/* Bouton déconnexion */}
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center px-4 py-3 text-sm font-bold text-white bg-gradient-to-r from-red-600/90 to-red-700/90 hover:from-red-600 hover:to-red-700 rounded-xl transition-all duration-300 border border-red-600/50 hover:border-red-500 shadow-lg hover:shadow-red-500/20 hover:scale-[1.02] active:scale-[0.98]"
          >
            <FiLogOut className="mr-2 w-4 h-4" />
            Déconnexion
          </button>
        </div>
      </aside>
    </div>
  );
}

export default AdminLayout;
